import {Suspense} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {useNavigate} from 'react-router';
import {diApi} from '@di-shared/api/di';
import {ERROR} from '@di-shared/const';
import Header from '@di-shared/features/common/Header';
import HeaderBackButton from '@di-shared/features/common/HeaderBackButton';
import Popper from '@di-shared/features/common/Popper';
import useApiError from '@di-shared/features/common/useApiError';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {HistoryTripDetailProps} from '@di-shared/features/history/trip-detail/HistoryTripDetail';
import {ROUTE_PATHS} from '@di-shared/routes';
import {roundToFixed} from '@di-shared/utils';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import useDashedDatetimeFormatter from '@di-shared/utils/useDashedDatetimeFormatter';
import useUnitConverter from '@di-shared/utils/useUnitConverter';
import {parseDateWithFormat} from '@shared/dateUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import usePendingHandler from '@shared/usePendingHandler';
import {useQueryClient, useSuspenseQuery} from '@tanstack/react-query';

export default function HistoryTripDetailKr({vin, tripId}: HistoryTripDetailProps) {
  return (
    <ErrorBoundary fallback={<FallbackError />}>
      <Suspense fallback={<FallbackLoading />}>
        <Inner
          vin={vin}
          tripId={tripId}
        />
      </Suspense>
    </ErrorBoundary>
  );
}

const MINUTES_PER_HOUR = 60;

function Inner({vin, tripId}: HistoryTripDetailProps) {
  const {t} = useInferableTranslation();
  const {format: formatCldrDate} = useCldrDatetimeFormatter();
  const {format: formatDashedDate} = useDashedDatetimeFormatter();
  const {convertDistanceValue, convertDistanceSymbol, convertSpeedValue, convertSpeedSymbol} = useUnitConverter();

  const {data: tripDetail} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.driveHistory.getTripDetail), vin, tripId],
    queryFn: () => diApi.driveHistory.getTripDetail({vin, tripId}),
    select: ({data}) => data,
  });

  const ignitionOnDate = parseDateWithFormat(tripDetail.ignOnTime);
  const ignitionOffDate = parseDateWithFormat(tripDetail.ignOffTime);

  const drivingHours = Math.floor(tripDetail.drvTime / MINUTES_PER_HOUR);
  const drivingMinutes = tripDetail.drvTime % MINUTES_PER_HOUR;

  const headerDate = ignitionOnDate ? formatCldrDate(ignitionOnDate, 'MMMMEEEEd') : ERROR.DASH;
  const startTime = ignitionOnDate ? formatDashedDate(ignitionOnDate, 'time') : ERROR.DASH;
  const endTime = ignitionOffDate ? formatDashedDate(ignitionOffDate, 'time') : ERROR.DASH;

  return (
    <div className="wrap">
      <Header left={<HeaderBackButton />}>
        <h1 className="title_xsmall_b nav_center_title">{headerDate}</h1>
      </Header>
      <main
        id="content"
        className="content page_my_trips"
      >
        <section className="section section_trip_detail">
          <div className="driving_summary">
            <dl className="driving_summary_list">
              <div className="driving_summary_item">
                <dt className="driving_summary_title">{t('T20435')}</dt>
                <dd className="driving_summary_info">
                  <span className="driving_summary_value">
                    {startTime} - {endTime}
                  </span>
                </dd>
              </div>
              <div className="driving_summary_item">
                <dt className="driving_summary_title">{t('T10053')}</dt>
                <dd className="driving_summary_info">
                  {drivingHours > 0 && (
                    <>
                      <span className="driving_summary_value">{drivingHours}</span>
                      <span className="driving_summary_unit">{t('T10054')}</span>
                    </>
                  )}
                  <span className="driving_summary_value">{drivingMinutes}</span>
                  <span className="driving_summary_unit">{t('T10055')}</span>
                </dd>
              </div>
              <div className="driving_summary_item">
                <dt className="driving_summary_title">{t('T10052')}</dt>
                <dd className="driving_summary_info">
                  {tripDetail.drvDist < 1 ? (
                    <span className="driving_summary_value">{t('T20341')}</span>
                  ) : (
                    <>
                      <span className="driving_summary_value">{convertDistanceValue(tripDetail.drvDist)}</span>
                      <span className="driving_summary_unit">{convertDistanceSymbol(tripDetail.drvDist)}</span>
                    </>
                  )}
                </dd>
              </div>
              <div className="driving_summary_item">
                <dt className="driving_summary_title">{t('T10205')}</dt>
                <dd className="driving_summary_info">
                  <span className="driving_summary_value">{convertSpeedValue(tripDetail.avgSpeed)}</span>
                  <span className="driving_summary_unit">{convertSpeedSymbol(tripDetail.avgSpeed)}</span>
                </dd>
              </div>
              <div className="driving_summary_item">
                <dt className="driving_summary_title">{t('T10206')}</dt>
                <dd className="driving_summary_info">
                  <span className="driving_summary_value">{convertSpeedValue(tripDetail.maxSpeed)}</span>
                  <span className="driving_summary_unit">{convertSpeedSymbol(tripDetail.maxSpeed)}</span>
                </dd>
              </div>
              {!tripDetail.evEnergyUsage && (
                <div className="driving_summary_item">
                  <dt className="driving_summary_title">{t('T10204')}</dt>
                  <dd className="driving_summary_info">
                    <span className="driving_summary_value">{tripDetail.idleTime}</span>
                    <span className="driving_summary_unit">{t('T10055')}</span>
                  </dd>
                </div>
              )}
              {tripDetail.evEnergyUsage && (
                <>
                  <div className="driving_summary_item">
                    <dt className="driving_summary_title">{t('T10071')}</dt>
                    <dd className="driving_summary_info">
                      <span className="driving_summary_value">
                        {roundToFixed(tripDetail.evEnergyUsage.totalPowerConsumption / 1000, 1)}
                      </span>
                      <span className="driving_summary_unit">kWh</span>
                    </dd>
                  </div>
                  <div className="driving_summary_item">
                    <dt className="driving_summary_title">{t('T20436')}</dt>
                    <dd className="driving_summary_info">
                      <span className="driving_summary_value">{roundToFixed(tripDetail.evEnergyUsage.regenPower / 1000, 1)}</span>
                      <span className="driving_summary_unit">kWh</span>
                    </dd>
                  </div>
                </>
              )}
            </dl>
          </div>
        </section>
        <section className="section section_tag">
          <HistoryTripTagsSection
            vin={vin}
            tripId={tripId}
            appliedTags={tripDetail.tags}
          />
        </section>
      </main>
    </div>
  );
}

function HistoryTripTagsSection({vin, tripId, appliedTags}: {vin: string; tripId: string; appliedTags: Array<{tagId: string; tagName: string}>}) {
  return (
    <ErrorBoundary fallback={<HistoryTripTagsSectionErrorFallback />}>
      <Suspense fallback={<HistoryTripTagsSectionLoading />}>
        <HistoryTripTagsSectionInner
          vin={vin}
          tripId={tripId}
          appliedTags={appliedTags}
        />
      </Suspense>
    </ErrorBoundary>
  );
}

function HistoryTripTagsSectionInner({
  vin,
  tripId,
  appliedTags,
}: {
  vin: string;
  tripId: string;
  appliedTags: Array<{tagId: string; tagName: string}>;
}) {
  const {t} = useInferableTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {resolveError} = useApiError();

  const {data: availableTags} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.tripTags.getAllTags)],
    queryFn: () => diApi.tripTags.getAllTags(),
    select: ({data}) => data.tags,
  });

  async function invalidateTripQueries() {
    await queryClient.invalidateQueries({
      queryKey: [getQueryKey(diApi.driveHistory.getTripDetail), vin, tripId],
    });
    await queryClient.invalidateQueries({
      queryKey: [getQueryKey(diApi.driveHistory.getFilteredTripHistory)],
    });
  }

  const [addTag] = usePendingHandler(
    async (tagId: string) => {
      await diApi.tripTags.addTagToTrip({vin, tripId, tagId});
      await invalidateTripQueries();
    },
    async (error) => {
      const [execute] = await resolveError(error);
      execute();
    }
  );

  const [removeTag] = usePendingHandler(
    async (tagId: string) => {
      await diApi.tripTags.removeTagFromTrip({vin, tripId, tagId});
      await invalidateTripQueries();
    },
    async (error) => {
      const [execute] = await resolveError(error);
      execute();
    }
  );

  async function toggleTag(tagId: string) {
    const isTagApplied = appliedTags.some((tag) => tag.tagId === tagId);

    if (isTagApplied) {
      await removeTag(tagId);
    } else {
      await addTag(tagId);
    }
  }

  return (
    <>
      <HistoryTripTagsSectionHeader />
      <div className="tag_area">
        {availableTags.map((tag) => {
          const isApplied = appliedTags.some((t) => t.tagId === tag.tagId);
          return (
            <button
              key={tag.tagId}
              type="button"
              className={isApplied ? 'btn btn_round is-selected' : 'btn btn_round'}
              onClick={() => toggleTag(tag.tagId)}
            >
              <span className="ic ic_check_16px" />
              {tag.tagName}
            </button>
          );
        })}
        <button
          type="button"
          className="btn btn_round btn_tag_add"
          onClick={() => {
            navigate(ROUTE_PATHS.HISTORY_TAGS.getFullPath(vin));
          }}
        >
          <span
            className="ic ic_add_14px"
            aria-hidden="true"
          />
          {t('T20416')}
        </button>
      </div>
    </>
  );
}

function HistoryTripTagsSectionHeader() {
  const {t} = useInferableTranslation();

  return (
    <div className="section_container_header">
      <h3 className="title_xxsmall">{t('T10354')}</h3>
      <Popper
        placement="bottom"
        className="tooltip"
        marginLeft={1.5}
        tooltip={
          <div
            tabIndex={0}
            role="tooltip"
          >
            <p className="tooltip_text">{t('T20437')}</p>
          </div>
        }
      >
        <div className="tooltip_area tooltip_area_single">
          <button
            type="button"
            className="btn_tooltip"
            aria-expanded="false"
          >
            <span
              className="ic ic_information_16px"
              aria-hidden="true"
            ></span>
            <span className="blind">{t('T10044')}</span>
          </button>
        </div>
      </Popper>
    </div>
  );
}

function HistoryTripTagsSectionLoading() {
  const {t} = useInferableTranslation();

  return (
    <>
      <HistoryTripTagsSectionHeader />
      <div className="tag_area">
        {Array.from({length: 3}).map((_, index) => (
          <span
            key={`tag-skeleton-${index}`}
            className="btn btn_round skeleton"
            style={{width: 72, height: 32}}
            aria-hidden="true"
          />
        ))}
      </div>
      <span className="blind">{t('T10354')}</span>
    </>
  );
}

function HistoryTripTagsSectionErrorFallback() {
  const {t} = useInferableTranslation();

  return (
    <>
      <HistoryTripTagsSectionHeader />
      <div className="trip_info">
        <span className="blind">trip info detail</span>
        <div className="empty_area">
          <span className="ic ic_caution_32px"></span>
          <span
            className="body_small_r"
            style={{whiteSpace: 'pre-wrap'}}
          >
            {t('T10175')}
          </span>
        </div>
      </div>
    </>
  );
}

function FallbackLoading() {
  const {t} = useInferableTranslation();
  return (
    <div className="wrap">
      <Header left={<HeaderBackButton />}>
        <h1 className="title_xsmall_b nav_center_title"></h1>
      </Header>
      <main
        id="content"
        className="content page_my_trips"
      >
        <section className="section section_trip_detail">
          <div className="driving_summary">
            <dl className="driving_summary_list">
              <div className="driving_summary_item">
                <dt className="driving_summary_title">{t('T20435')}</dt>
                <dd
                  className="driving_summary_info skeleton"
                  style={{width: 100}}
                >
                  <span className="driving_summary_value">&nbsp;</span>
                </dd>
              </div>
              <div className="driving_summary_item">
                <dt className="driving_summary_title">{t('T10053')}</dt>
                <dd
                  className="driving_summary_info skeleton"
                  style={{width: 100}}
                >
                  <span className="driving_summary_value">&nbsp;</span>
                </dd>
              </div>
              <div className="driving_summary_item">
                <dt className="driving_summary_title">{t('T10052')}</dt>
                <dd
                  className="driving_summary_info skeleton"
                  style={{width: 100}}
                >
                  <span className="driving_summary_value">&nbsp;</span>
                </dd>
              </div>
              <div className="driving_summary_item">
                <dt className="driving_summary_title">{t('T10205')}</dt>
                <dd
                  className="driving_summary_info skeleton"
                  style={{width: 100}}
                >
                  <span className="driving_summary_value">&nbsp;</span>
                </dd>
              </div>
              <div className="driving_summary_item">
                <dt className="driving_summary_title">{t('T10206')}</dt>
                <dd
                  className="driving_summary_info skeleton"
                  style={{width: 100}}
                >
                  <span className="driving_summary_value">&nbsp;</span>
                </dd>
              </div>
            </dl>
          </div>
          <section className="section section_tag">
            <HistoryTripTagsSectionLoading />
          </section>
        </section>
      </main>
    </div>
  );
}

function FallbackError() {
  const {t} = useInferableTranslation();
  const {defaultDistanceSymbol, defaultSpeedSymbol} = useUnitConverter();
  return (
    <div className="wrap">
      <Header left={<HeaderBackButton />}>
        <h1 className="title_xsmall_b nav_center_title">{ERROR.DASH}</h1>
      </Header>
      <main
        id="content"
        className="content page_my_trips"
      >
        <section className="section section_trip_detail">
          <div className="driving_summary">
            <dl className="driving_summary_list">
              <div className="driving_summary_item">
                <dt className="driving_summary_title">{t('T20435')}</dt>
                <dd className="driving_summary_info">
                  <span className="driving_summary_value">{ERROR.DASH}</span>
                </dd>
              </div>
              <div className="driving_summary_item">
                <dt className="driving_summary_title">{t('T10053')}</dt>
                <dd className="driving_summary_info">
                  <span className="driving_summary_value">{ERROR.DASH}</span>
                  <span className="driving_summary_unit">{t('T10054')}</span>
                  <span className="driving_summary_value">{ERROR.DASH}</span>
                  <span className="driving_summary_unit">{t('T10055')}</span>
                </dd>
              </div>
              <div className="driving_summary_item">
                <dt className="driving_summary_title">{t('T10052')}</dt>
                <dd className="driving_summary_info">
                  <span className="driving_summary_value">{ERROR.DASH}</span>
                  <span className="driving_summary_unit">{defaultDistanceSymbol}</span>
                </dd>
              </div>
              <div className="driving_summary_item">
                <dt className="driving_summary_title">{t('T10205')}</dt>
                <dd className="driving_summary_info">
                  <span className="driving_summary_value">{ERROR.DASH}</span>
                  <span className="driving_summary_unit">{defaultSpeedSymbol}</span>
                </dd>
              </div>
              <div className="driving_summary_item">
                <dt className="driving_summary_title">{t('T10206')}</dt>
                <dd className="driving_summary_info">
                  <span className="driving_summary_value">{ERROR.DASH}</span>
                  <span className="driving_summary_unit">{defaultSpeedSymbol}</span>
                </dd>
              </div>
            </dl>
          </div>
          <div className="trip_info">
            <div className="empty_area">
              <span className="ic ic_caution_32px"></span>
              <span
                className="body_small_r"
                style={{whiteSpace: 'pre-wrap'}}
              >
                {t('T10175')}
              </span>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
